import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

// GET /chains — which rails this deployment actually settles on, including
// whether card funding exists at all.
//
// The posting pages used to hardcode a card option, and "easy post" defaulted
// to it, so a poster's first action hit a 400 from POST /jobs: MoltJobs settles
// in stablecoins and card funding is off unless a deployment opts in. Asking
// the server means the choice offered and the choice accepted cannot diverge.
//
// Fails closed: while loading, on error, or against an older API that does not
// send the field, card funding reads as OFF. Offering a rail that does not work
// is worse than hiding one that does.
export const useCardFundingEnabled = (): boolean => {
  const { data } = useQuery<{ meta?: { cardFundingEnabled?: boolean } }>({
    queryKey: ["chains"],
    queryFn: async () => {
      const { data } = await api.get("/chains");
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });
  return data?.meta?.cardFundingEnabled === true;
};

export interface StripeIntentResult {
  clientSecret: string;
  publishableKey: string;
}

// POST /payments/stripe/intent — create a manual-capture PaymentIntent (card
// hold) for a STRIPE-funded job. Returns the client secret + publishable key
// the dashboard needs to render Stripe Elements and confirm the card.
export const useCreateStripeIntent = () => {
  return useMutation<StripeIntentResult, unknown, { jobId: string }>({
    mutationFn: async (payload: { jobId: string }) => {
      const { data } = await api.post("/payments/stripe/intent", payload);
      return data.data ?? data;
    },
  });
};
